/**
 * Every speech-to-text engine a locale can be transcribed by. The values match the `engine` field on
 * `SpeechTranscriptionEvent` and the constants in the backend's `stt-constants.ts`, so a dashboard
 * query and this table use the same names.
 */
export type Transcriber = 'azure' | 'azure-fast' | 'microsoft-ai';

/**
 * Our canonical set of supported language sub-tags (weavix refers to these as "locales"),
 * their region sub-tags (weavix refers to these as "dialects"), and which engines can
 * transcribe each one.
 *
 * Assume the first element in `dialects` is the default.
 *
 * `transcribers` lives here so that adding a language stays one edit -- a second list is what made
 * it painful before. It records what each vendor supports, measured against their live API and
 * intersected with what we ship.
 *
 * Published coverage per engine:
 * - azure (Speech SDK): https://learn.microsoft.com/azure/ai-services/speech-service/language-support?tabs=stt
 * `azure-fast` reaches the same vendor models as `azure` over a different API, so it is
 * deliberately absent from the per-language `transcribers` lists below: its coverage is resolved
 * through `azure` by the backend's `stt-language-support.ts` rather than duplicated here, where the
 * two could drift.
 *
 * - microsoft-ai (mai-transcribe-2 on LLM Speech): coverage probed against the live service rather
 *   than taken from the published table, which claims 60 languages without enumerating them
 *
 * Per-engine wire quirks -- where a vendor wants a different spelling of a code it does support --
 * live in the backend's `stt-language-support.ts`, not here.
 */
export const SUPPORTED_LANGUAGE_TAGS = {
    en: {
        dialects: ['US'],
        transcribers: ['azure', 'microsoft-ai'],
    },
    es: {
        dialects: ['MX', 'AR', 'BO', 'CL', 'CO', 'CR', 'CU', 'DO', 'EC', 'ES', 'GQ', 'GT', 'HN', 'NI', 'PA', 'PE', 'PR', 'PY', 'SV', 'US', 'UY', 'VE'],
        transcribers: ['azure', 'microsoft-ai'],
    },
    pt: {
        dialects: ['BR'],
        transcribers: ['azure', 'microsoft-ai'],
    },
    de: {
        dialects: ['DE'],
        transcribers: ['azure', 'microsoft-ai'],
    },
    it: {
        dialects: ['IT'],
        transcribers: ['azure', 'microsoft-ai'],
    },
    fr: {
        dialects: ['FR'],
        transcribers: ['azure', 'microsoft-ai'],
    },
    my: {
        dialects: ['MM'],
        transcribers: ['azure'],
    },
    sw: {
        dialects: ['KE', 'TZ'],
        transcribers: ['azure', 'microsoft-ai'],
    },
    sq: {
        dialects: ['AL'],
        transcribers: ['azure'],
    },
    ko: {
        dialects: ['KR'],
        transcribers: ['azure', 'microsoft-ai'],
    },
    th: {
        dialects: ['TH'],
        transcribers: ['azure', 'microsoft-ai'],
    },
    fil: {
        dialects: ['PH'],
        transcribers: ['azure', 'microsoft-ai'],
    },
    ps: {
        dialects: ['AF'],
        transcribers: ['azure'],
    },
    ja: {
        dialects: ['JP'],
        transcribers: ['azure', 'microsoft-ai'],
    },
    vi: {
        dialects: ['VN'],
        transcribers: ['azure', 'microsoft-ai'],
    },
    zh: {
        dialects: ['CN'],
        transcribers: ['azure', 'microsoft-ai'],
    },
    uk: {
        dialects: ['UA'],
        transcribers: ['azure', 'microsoft-ai'],
    },
    lo: {
        dialects: ['LA'],
        transcribers: ['azure'],
    },
    ms: {
        dialects: ['MY'],
        transcribers: ['azure', 'microsoft-ai'],
    },
    fa: {
        dialects: ['IR'],
        transcribers: ['azure', 'microsoft-ai'],
    },
    ne: {
        dialects: ['NP'],
        transcribers: ['azure', 'microsoft-ai'],
    },
    ru: {
        dialects: ['RU'],
        transcribers: ['azure', 'microsoft-ai'],
    },
    am: {
        dialects: ['ET'],
        transcribers: ['azure'],
    },
    ar: {
        dialects: ['AE', 'BH', 'DZ', 'EG', 'IQ', 'JO', 'KW', 'LB', 'LY', 'MA', 'OM', 'QA', 'SA', 'SY', 'TN', 'YE'],
        transcribers: ['azure', 'microsoft-ai'],
    },
    hi: {
        dialects: ['IN'],
        transcribers: ['azure', 'microsoft-ai'],
    },
    km: {
        dialects: ['KH'],
        transcribers: ['azure'],
    },
    nl: {
        dialects: ['NL', 'BE'],
        transcribers: ['azure', 'microsoft-ai'],
    },
} as const satisfies {
    [tag: string]: {
        dialects: ReadonlyArray<string> & { 0: string };
        transcribers: ReadonlyArray<Transcriber>;
    };
};

/**
 * A supported locale e.g. "en", "es", "pt".
 */
export type i18nLocale = keyof typeof SUPPORTED_LANGUAGE_TAGS;

/**
 * The list of supported locales.
 */
export const I18N_LOCALES = Object.keys(SUPPORTED_LANGUAGE_TAGS) as i18nLocale[];

/**
 * A dialect, restricted to the given locale.
 */
export type Dialect<T extends i18nLocale> = typeof SUPPORTED_LANGUAGE_TAGS[T]['dialects'][number];

/**
 * Any supported dialect, for all locales.
 *
 * This is a union of all dialects, so not every combination with {@link i18nLocale} is valid.
 */
export type i18nDialect = Dialect<i18nLocale>;

/**
 * A list of supported dialects.
 *
 * This is a collection of all dialects, so not every combination with {@link I18N_LOCALES} is valid.
 */
export const I18N_DIALECTS = Object.values(SUPPORTED_LANGUAGE_TAGS).flatMap(tag => tag.dialects);

/**
 * A supported locale-dialect pair.
 *
 * Use the `normalizeLocale` function in `@weavix/domain` to safely get this.
 */
export type i18nFullLanguage = {
    [T in i18nLocale]: `${T}-${Dialect<T>}`;
}[i18nLocale];

/**
 * The list of supported locale-dialect pairs.
 */
export const I18N_FULL_LANGUAGES = Object.entries(SUPPORTED_LANGUAGE_TAGS)
    .flatMap(([loc, tag]) => tag.dialects.map(dialect => `${loc}-${dialect}` as i18nFullLanguage));

/** True when `transcriber` can transcribe `locale` at all. */
export function transcriberSupportsLocale(transcriber: Transcriber, locale: i18nLocale): boolean {
    return (SUPPORTED_LANGUAGE_TAGS[locale].transcribers as ReadonlyArray<Transcriber>).includes(transcriber);
}

/**
 * The native name of each language.
 *
 * These should never be translated, so they don't belong in the translation files.
 */
export const NATIVE_LANGUAGE_NAMES = {
    en: 'English',
    es: 'Español',
    'es-AR': 'Español (Argentina)',
    'es-BO': 'Español (Bolivia)',
    'es-CL': 'Español (Chile)',
    'es-CO': 'Español (Colombia)',
    'es-CR': 'Español (Costa Rica)',
    'es-CU': 'Español (Cuba)',
    'es-DO': 'Español (República Dominicana)',
    'es-EC': 'Español (Ecuador)',
    'es-ES': 'Español (España)',
    'es-GQ': 'Español (Guinea Ecuatorial)',
    'es-GT': 'Español (Guatemala)',
    'es-HN': 'Español (Honduras)',
    'es-MX': 'Español (México)',
    'es-NI': 'Español (Nicaragua)',
    'es-PA': 'Español (Panamá)',
    'es-PE': 'Español (Perú)',
    'es-PR': 'Español (Puerto Rico)',
    'es-PY': 'Español (Paraguay)',
    'es-SV': 'Español (El Salvador)',
    'es-US': 'Español (Estados Unidos)',
    'es-UY': 'Español (Uruguay)',
    'es-VE': 'Español (Venezuela)',
    pt: 'Português',
    de: 'Deutsch',
    it: 'Italiano',
    fr: 'Français',
    my: 'မြန်မာ',
    sw: 'Kiswahili',
    'sw-KE': 'Kiswahili (Kenya)',
    'sw-TZ': 'Kiswahili (Tanzania)',
    sq: 'Shqiptare',
    ko: '한국인',
    th: 'ภาษาไทย',
    fil: 'Filipino',
    ps: 'پښتو',
    ja: '日本語',
    vi: 'Tiếng Việt',
    zh: '中国人',
    uk: 'Українська',
    lo: 'ພາສາລາວ',
    ms: 'Melayu',
    fa: 'فارسی',
    ne: 'नेपाली',
    ru: 'Русский',
    am: 'አማርኛ',
    ar: 'العربية',
    'ar-AE': 'العربية (الإمارات العربية المتحدة)',
    'ar-BH': 'العربية (البحرين)',
    'ar-DZ': 'العربية (الجزائر)',
    'ar-EG': 'العربية (مصر)',
    'ar-IQ': 'العربية (العراق)',
    'ar-JO': 'العربية (الأردن)',
    'ar-KW': 'العربية (الكويت)',
    'ar-LB': 'العربية (لبنان)',
    'ar-LY': 'العربية (ليبيا)',
    'ar-MA': 'العربية (المغرب)',
    'ar-OM': 'العربية (عمان)',
    'ar-QA': 'العربية (قطر)',
    'ar-SA': 'العربية (السعودية)',
    'ar-SY': 'العربية (سوريا)',
    'ar-TN': 'العربية (تونس)',
    'ar-YE': 'العربية (اليمن)',
    hi: 'हिन्दी',
    km: 'ខ្មែរ',
    nl: 'Nederlands',
    'nl-NL': 'Nederlands (Nederland)',
    'nl-BE': 'Nederlands (België)',
} as const satisfies {
    [key in i18nFullLanguage]: string;
} | {
    [key in i18nLocale]: string;
};

export interface TranslateInputResponse {
    translations: { [key in i18nLocale]?: string };
}

export interface TranslateInputRequest {
    message: string;
    sourceLocale?: i18nLocale;
    sourceDialect?: i18nDialect;
    targetLocale: i18nLocale;
    targetDialect?: i18nDialect;
}

/**
 * A single completed translation exchange to persist to the translator session
 * history. Sent once per utterance — after the final transcription and its
 * translation are known — so that in-progress/interim translations are never
 * recorded. Persistence is gated behind the Walt translator feature flag.
 */
export interface CaptureTranslationRequest {
    /**
     * Client-supplied identifier grouping all messages from a single translator
     * session so a full back-and-forth conversation can be reconstructed.
     */
    translationSessionId?: string;

    /** The final transcribed source text that was translated. */
    transcript: string;

    /** The final translated text shown to the user. */
    translation: string;

    sourceLocale?: i18nLocale;
    sourceDialect?: i18nDialect;
    targetLocale: i18nLocale;
    targetDialect?: i18nDialect;

    /**
     * Optional URL of the source audio recording captured by speech-to-text for
     * this utterance, so the original audio can be retrieved later.
     */
    recordingUrl?: string;

    /** The client's id for this exchange (its turn id), so a capture sent twice is recorded once. */
    captureId?: string;

    /** True when the exchange was translated on device with no connection and sent later from the walt's queue. */
    offline?: boolean;

    /** When the exchange happened (ISO 8601), for a capture sent later; the server's clock when absent. */
    capturedAt?: string;
}
